import {
  type Project,
  type ProjectMember,
  type AssignableProjectMemberRole,
  type ProjectMemberRole,
} from '@littlebird/sdk';
import {
  PROJECT_MEMBER_ROLE_DESCRIPTIONS,
  PROJECT_MEMBER_ROLE_LABELS,
  buildInviteSuggestions,
  buildProjectJoinUrl,
  mapShareError,
  projectMemberRowCopy,
  type InviteSuggestion,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { useHasOpenModals } from '@/components/modals/modalsManager';
import { Text, XStack, YStack } from '@/components/ui';
import { InitialsAvatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { IconButton } from '@/components/ui/IconButton';
import { getEnvVar } from '@/env';
import { FEATURES, useFeature } from '@/features/feature-flags';
import {
  AddressConnectionButton,
  ConnectionButton,
  LOOKUP_DELAY_MS,
  useIsConnecting,
  usePeopleWikiQuery,
  type WikiPerson,
} from '@/features/people-wiki';
import { useMyTeamQuery } from '@/features/team/team.query';
import { useHasSettled } from '@/hooks/useHasSettled';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import {
  useAddMember,
  useProjectMembers,
  useRemoveMember,
  useShareLink,
  useUpdateMemberRole,
} from './project-members.query';
import { useRefocusAfterBusy } from './use-refocus-after-busy';

const LINK_ON_LABEL = 'Anyone in your team with the link';
const LINK_OFF_LABEL = 'Only people you invite';

const ASSIGNABLE_ROLES: readonly AssignableProjectMemberRole[] = [
  'admin',
  'member',
];
const GRANTABLE_ROLES: readonly ProjectMemberRole[] = [
  'owner',
  'admin',
  'member',
];

const TYPED_ADDRESS_NOTE = 'Not in your People Wiki yet';

export const ShareProjectModal = observer(function ShareProjectModal({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { canInvite, canManageMembers, canManageLink } = project.capabilities;

  const { data: members, isLoading } = useProjectMembers(project.id, open);
  const addMemberMutation = useAddMember(project.id);
  const updateRoleMutation = useUpdateMemberRole(project.id);
  const removeMemberMutation = useRemoveMember(project.id);
  const shareLink = useShareLink(project.id, open, canManageLink);
  const { data: team } = useMyTeamQuery(open && canInvite);
  const hasFeature = useFeature();
  // A dialog that is closed has no picker to suggest anyone in, so it neither loads the wiki nor maps its people
  const suggestsWikiPeople =
    open && canInvite && hasFeature(FEATURES.PEOPLE_WIKI);
  const { data: wikiPeople } = usePeopleWikiQuery({
    enabled: suggestsWikiPeople,
  });

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] =
    useState<AssignableProjectMemberRole>('member');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  // The buttons of the rows look up their addresses, so they wait until the user has stopped typing
  const hasSettledQuery = useHasSettled(inviteEmail, LOOKUP_DELAY_MS);
  const pickerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement>(null);
  const hasOpenModal = useHasOpenModals();
  const isConnecting = useIsConnecting();
  // A modal that a row opens takes the focus, and the button of a connection under way is disabled and drops it, with
  // no blur to say so. The picker stays open meanwhile. After, the field gets the focus back when nobody has it, and
  // the picker closes when the user has moved on to a control outside it.
  useRefocusAfterBusy({
    isBusy: hasOpenModal || isConnecting,
    isOpen: isPickerOpen,
    field: fieldRef,
    picker: pickerRef,
    onLeave: () => setIsPickerOpen(false),
  });

  const linkUrl = shareLink.token
    ? buildProjectJoinUrl(
        shareLink.token,
        getEnvVar('BASE_URL_FOR_PUBLIC_LINKS') ?? window.location.origin
      )
    : null;

  // Nobody is suggested before the wiki has been read, because its pages could hold the address that the user types
  const readWikiPeople = suggestsWikiPeople ? wikiPeople : undefined;

  const wikiPeopleById = useMemo(
    () => new Map((readWikiPeople ?? []).map((person) => [person.id, person])),
    [readWikiPeople]
  );

  const invitePeople = useMemo(
    () =>
      readWikiPeople?.map(
        ({ id, name, isRequestOnly, primaryEmail, otherEmails }) => ({
          id,
          name,
          isRequestOnly,
          emails: [primaryEmail, ...otherEmails].filter(
            (email) => email !== null
          ),
        })
      ) ?? null,
    [readWikiPeople]
  );

  const suggestions = useMemo<InviteSuggestion[]>(
    () =>
      buildInviteSuggestions({
        team: team ? { team: team.team, members: team.members } : null,
        members: members ?? null,
        people: invitePeople,
        query: inviteEmail,
      }),
    [team, members, invitePeople, inviteEmail]
  );

  const invite = async (
    input: { email: string; teamId?: never } | { teamId: string; email?: never }
  ) => {
    try {
      await addMemberMutation.mutateAsync(
        input.teamId !== undefined
          ? { teamId: input.teamId, role: inviteRole }
          : { email: input.email, role: inviteRole }
      );
      trackEvent('PROJECT_SHARE_COMPLETED', {
        role: inviteRole,
        target: input.teamId ? 'team' : 'user',
      });
      setInviteEmail('');
      setIsPickerOpen(false);
    } catch (error) {
      toast.error(mapShareError(error));
    }
  };

  const handleRoleChange = async (
    membershipId: string,
    role: ProjectMemberRole
  ) => {
    try {
      await updateRoleMutation.mutateAsync({ membershipId, role });
    } catch (error) {
      toast.error(mapShareError(error));
    }
  };

  const handleRemove = async (membershipId: string) => {
    try {
      await removeMemberMutation.mutateAsync(membershipId);
    } catch (error) {
      toast.error(mapShareError(error));
    }
  };

  const handleToggleLink = async (enabled: boolean) => {
    if (enabled) {
      const token = await shareLink.mint();
      if (token) {
        trackEvent('PROJECT_SHARE_COMPLETED', {
          role: 'member',
          target: 'link',
        });
      }
      return;
    }
    await shareLink.revoke();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content showCloseButton className="no-record">
        <YStack className="gap-6">
          <Dialog.Title className="pr-8">
            Share &ldquo;{project.name}&rdquo;
          </Dialog.Title>

          {canInvite && (
            <YStack
              ref={pickerRef}
              className="relative"
              onFocus={() => setIsPickerOpen(true)}
              onBlur={(event) => {
                // A modal takes the focus from the picker, and a connection under way drops it to the page, which is a
                // blur with no new target. Neither means that the user left, but moving on to another control does.
                const next = event.relatedTarget;
                const isHeld = hasOpenModal || (isConnecting && !next);
                const isInside =
                  next instanceof Node && event.currentTarget.contains(next);
                if (!isHeld && !isInside) setIsPickerOpen(false);
              }}
            >
              <XStack className="items-center gap-2 rounded-lg border border-border-strong bg-surface py-1.5 pl-3 pr-1.5 focus-within:border-accent">
                <Icons.search className="size-4 shrink-0 text-foreground-muted" />
                <input
                  ref={fieldRef}
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  aria-label="Share with a name or email"
                  placeholder="Search people or enter an email"
                  className="h-7 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && inviteEmail.trim()) {
                      event.preventDefault();
                      void invite({ email: inviteEmail.trim() });
                    }
                  }}
                />
                {inviteEmail.trim() && (
                  <XStack className="shrink-0 items-center gap-1">
                    <RoleDropdown
                      role={inviteRole}
                      options={ASSIGNABLE_ROLES}
                      onChange={setInviteRole}
                    />
                    <Button
                      size="xs"
                      onClick={() => void invite({ email: inviteEmail.trim() })}
                      disabled={addMemberMutation.isPending}
                    >
                      Share
                    </Button>
                  </XStack>
                )}
              </XStack>

              {isPickerOpen && suggestions.length > 0 && (
                <YStack className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 gap-0.5 overflow-y-auto rounded-lg border border-border-strong bg-background-subtle p-1 shadow-md">
                  {suggestions.map((suggestion) => (
                    <SuggestionRow
                      key={suggestion.key}
                      suggestion={suggestion}
                      person={
                        hasSettledQuery && suggestion.personId
                          ? wikiPeopleById.get(suggestion.personId)
                          : undefined
                      }
                      disabled={addMemberMutation.isPending}
                      onSelect={() => {
                        if (suggestion.isTeam && team) {
                          void invite({ teamId: team.team.id });
                        } else {
                          setInviteEmail(suggestion.detail);
                        }
                      }}
                    />
                  ))}
                </YStack>
              )}
            </YStack>
          )}

          <YStack className="gap-3">
            <Text size="sm" variant="muted" as="span">
              Who has access
            </Text>

            {(canManageLink || project.hasShareLink) && (
              <AccessSelector
                linkOn={project.hasShareLink}
                canManage={canManageLink}
                busy={shareLink.isMinting || shareLink.isRevoking}
                onChange={(enabled) => void handleToggleLink(enabled)}
              />
            )}

            <YStack className="gap-1">
              {isLoading ? (
                <Text size="sm" variant="muted" as="span">
                  Loading…
                </Text>
              ) : (
                members?.map((member) => (
                  <MemberRow
                    key={member.membershipId ?? `owner-${member.userId}`}
                    member={member}
                    canManageMembers={canManageMembers}
                    canGrantOwner={project.role === 'owner'}
                    onRoleChange={(role) =>
                      member.membershipId &&
                      void handleRoleChange(member.membershipId, role)
                    }
                    onRemove={() =>
                      member.membershipId &&
                      void handleRemove(member.membershipId)
                    }
                  />
                ))
              )}
            </YStack>
          </YStack>

          {linkUrl && project.hasShareLink && (
            <XStack className="items-center justify-between gap-3 border-t border-border pt-4">
              <Text
                size="xs"
                variant="muted"
                as="span"
                className="min-w-0 flex-1 truncate"
              >
                {linkUrl}
              </Text>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-2"
                onClick={() => {
                  void navigator.clipboard.writeText(linkUrl);
                  toast.success('Link copied');
                }}
              >
                <Icons.link className="size-3.5" />
                Copy link
              </Button>
            </XStack>
          )}
        </YStack>
      </Dialog.Content>
    </Dialog.Root>
  );
});

function SuggestionRow({
  suggestion,
  person,
  disabled,
  onSelect,
}: {
  suggestion: InviteSuggestion;
  /** The wiki person behind the row */
  person?: WikiPerson;
  disabled: boolean;
  onSelect: () => void;
}) {
  const connection = person ? (
    <ConnectionButton person={person} surface="share_picker" />
  ) : (
    suggestion.typedEmail && (
      <AddressConnectionButton
        email={suggestion.detail}
        surface="share_picker"
      />
    )
  );

  // Teammates and the whole team can be shared with; wiki people and typed addresses are outside the team
  const isShareable = !suggestion.personId && !suggestion.typedEmail;

  const label = (
    <>
      {suggestion.isTeam ? (
        <XStack className="size-7 shrink-0 items-center justify-center rounded-full bg-surface-active">
          <Icons.attendees className="size-3.5 text-foreground" />
        </XStack>
      ) : (
        <InitialsAvatar name={suggestion.name} />
      )}
      <YStack className="min-w-0 flex-1 items-start">
        <Text size="sm" as="span" className="w-full truncate text-left">
          {suggestion.name}
        </Text>
        <Text
          size="xs"
          variant="muted"
          as="span"
          className="w-full truncate text-left"
        >
          {suggestion.typedEmail ? TYPED_ADDRESS_NOTE : suggestion.detail}
        </Text>
      </YStack>
    </>
  );

  return (
    <XStack
      role="group"
      aria-label={suggestion.name}
      className="items-center gap-1 pr-1"
    >
      {isShareable ? (
        // Not a Button, whose content is centered in a column as wide as the content: a long address would spill out
        // of both sides of the row instead of being cut at its end
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-secondary-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          disabled={disabled}
          onClick={onSelect}
        >
          {label}
        </button>
      ) : (
        // The server shares a project only with a teammate, so a person from outside the team is not picked for
        // sharing: the row has no hover or selection, and Connect and Invite beside it are the actions
        <div className="flex min-w-0 flex-1 items-center gap-3 px-2 py-1.5 text-left">
          {label}
        </div>
      )}
      {connection && <div className="shrink-0">{connection}</div>}
    </XStack>
  );
}

function AccessSelector({
  linkOn,
  canManage,
  busy,
  onChange,
}: {
  linkOn: boolean;
  canManage: boolean;
  busy: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const Icon = linkOn ? Icons.link : Icons.lock;
  const label = linkOn ? LINK_ON_LABEL : LINK_OFF_LABEL;

  if (!canManage) {
    return (
      <XStack className="items-center gap-2 py-1">
        <Icon className="size-4 shrink-0 text-foreground-muted" />
        <Text size="sm" as="span">
          {label}
        </Text>
      </XStack>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 w-fit gap-2"
          disabled={busy}
        >
          <Icon className="size-4 text-foreground-muted" />
          {label}
          <Icons.chevronDown className="size-3" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" fit>
        <DropdownMenu.Item onSelect={() => onChange(false)}>
          {LINK_OFF_LABEL}
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => onChange(true)}>
          {LINK_ON_LABEL}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

function MemberRow({
  member,
  canManageMembers,
  canGrantOwner,
  onRoleChange,
  onRemove,
}: {
  member: ProjectMember;
  canManageMembers: boolean;
  canGrantOwner: boolean;
  onRoleChange: (role: ProjectMemberRole) => void;
  onRemove: () => void;
}) {
  const { name, isYou, detail } = projectMemberRowCopy(
    member,
    userStore.user?.email
  );
  const role = member.role;
  const hasJoined = member.userId !== null;
  const roleOptions =
    canGrantOwner && hasJoined ? GRANTABLE_ROLES : ASSIGNABLE_ROLES;

  return (
    <XStack className="items-center gap-3 py-1">
      {member.teamId ? (
        <XStack className="size-7 shrink-0 items-center justify-center rounded-full bg-surface-active">
          <Icons.attendees className="size-3.5 text-foreground" />
        </XStack>
      ) : (
        <InitialsAvatar name={name} />
      )}
      <YStack className="min-w-0 flex-1">
        <Text size="sm" as="span" className="truncate">
          {name}
          {isYou && <span className="text-foreground-muted"> (you)</span>}
        </Text>
        {detail && (
          <Text size="xs" variant="muted" as="span" className="truncate">
            {detail}
          </Text>
        )}
      </YStack>
      {member.isOwner ? (
        <Text size="sm" variant="muted" as="span" className="shrink-0">
          Owner
        </Text>
      ) : canManageMembers ? (
        <XStack className="shrink-0 items-center gap-1">
          <RoleDropdown
            role={role}
            options={roleOptions}
            onChange={onRoleChange}
          />
          <IconButton
            icon={Icons.trash}
            size="sm"
            onClick={onRemove}
            tooltip="Remove"
          />
        </XStack>
      ) : (
        <Text size="sm" variant="muted" as="span" className="shrink-0">
          {PROJECT_MEMBER_ROLE_LABELS[role]}
        </Text>
      )}
    </XStack>
  );
}

function RoleDropdown<Role extends ProjectMemberRole>({
  role,
  options,
  onChange,
}: {
  role: Role;
  options: readonly Role[];
  onChange: (role: Role) => void;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="xs" className="gap-1">
          {PROJECT_MEMBER_ROLE_LABELS[role]}
          <Icons.chevronDown className="size-3" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className="w-64">
        {options.map((option) => (
          <DropdownMenu.Item key={option} onSelect={() => onChange(option)}>
            <YStack className="gap-0.5">
              <span>{PROJECT_MEMBER_ROLE_LABELS[option]}</span>
              <Text size="xs" variant="muted" as="span">
                {PROJECT_MEMBER_ROLE_DESCRIPTIONS[option]}
              </Text>
            </YStack>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
